"use client";

import {
  AnimatePresence,
  type HTMLMotionProps,
  motion,
  useReducedMotion,
  type Variants,
} from "motion/react";
import {
  createContext,
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { SharedLayoutBackground } from "./shared-layout-background";

const SIDEBAR_SHORTCUT = "b";

export const ANIMATED_SIDEBAR_EXPANDED_WIDTH = 208;
// The 48px rail leaves a 4px gutter around the sidebar's 40px interaction targets.
export const ANIMATED_SIDEBAR_COLLAPSED_WIDTH = 48;

const panelTransition = {
  type: "spring",
  stiffness: 380,
  damping: 35,
  mass: 0.75,
} as const;

export const animatedSidebarSpringLayout = {
  type: "spring",
  stiffness: 360,
  damping: 32,
  mass: 0.6,
} as const;

export const animatedSidebarSpringPress = {
  type: "spring",
  stiffness: 500,
  damping: 30,
  mass: 0.6,
} as const;

const easeOut = [0.16, 1, 0.3, 1] as const;
const reducedTransition = { duration: 0.16, ease: easeOut } as const;
const labelEnterTransition = { duration: 0.2, delay: 0.08, ease: easeOut } as const;
const labelExitTransition = { duration: 0.12, ease: easeOut } as const;

const submenuVariants: Variants = {
  closed: {
    height: 0,
    opacity: 0,
    clipPath: "inset(0 0 100% 0 round 8px)",
    transition: {
      duration: 0.14,
      ease: easeOut,
      staggerChildren: 0.025,
      staggerDirection: -1,
    },
  },
  open: {
    height: "auto",
    opacity: 1,
    clipPath: "inset(0 0 0% 0 round 8px)",
    transition: {
      duration: 0.2,
      delayChildren: 0.035,
      ease: easeOut,
      staggerChildren: 0.045,
    },
  },
};

const submenuItemVariants: Variants = {
  closed: { opacity: 0, y: -6, filter: "blur(3px)" },
  open: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.18, ease: easeOut },
  },
};

export function getAnimatedSidebarSubmenuVariants(reduceMotion: boolean): Variants | undefined {
  return reduceMotion ? undefined : submenuVariants;
}

export function getAnimatedSidebarSubmenuItemVariants(reduceMotion: boolean): Variants | undefined {
  return reduceMotion ? undefined : submenuItemVariants;
}

interface AnimatedSidebarContextValue {
  activeLayoutId: string;
  expanded: boolean;
  isMobile: boolean;
  reduceMotion: boolean;
  setExpanded: (expanded: boolean) => void;
  toggle: () => void;
}

const AnimatedSidebarContext = createContext<AnimatedSidebarContextValue | null>(null);

export function useAnimatedSidebar(): AnimatedSidebarContextValue {
  const context = useContext(AnimatedSidebarContext);
  if (!context) throw new Error("useAnimatedSidebar must be used within AnimatedSidebarProvider");
  return context;
}

export function AnimatedSidebarProvider({
  children,
  defaultExpanded = true,
}: {
  children: ReactNode;
  defaultExpanded?: boolean;
}): React.ReactElement {
  const [expanded, setExpandedState] = useState(defaultExpanded);
  const activeLayoutId = `${useId()}-active`;
  const isMobile = useIsMobile();
  const reduceMotion = useReducedMotion() ?? false;
  const setExpanded = useCallback((nextExpanded: boolean) => {
    setExpandedState(nextExpanded);
  }, []);
  const toggle = useCallback(() => {
    if (!isMobile) setExpanded(!expanded);
  }, [expanded, isMobile, setExpanded]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== SIDEBAR_SHORTCUT || (!event.metaKey && !event.ctrlKey)) {
        return;
      }
      event.preventDefault();
      toggle();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggle]);

  const value = useMemo(
    () => ({
      activeLayoutId,
      expanded: isMobile || expanded,
      isMobile,
      reduceMotion,
      setExpanded,
      toggle,
    }),
    [activeLayoutId, expanded, isMobile, reduceMotion, setExpanded, toggle],
  );

  return (
    <AnimatedSidebarContext.Provider value={value}>{children}</AnimatedSidebarContext.Provider>
  );
}

export function AnimatedSidebarPanel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): React.ReactElement {
  const { expanded, isMobile, reduceMotion } = useAnimatedSidebar();

  return (
    <motion.aside
      animate={{
        width:
          expanded || isMobile ? ANIMATED_SIDEBAR_EXPANDED_WIDTH : ANIMATED_SIDEBAR_COLLAPSED_WIDTH,
      }}
      initial={false}
      transition={reduceMotion ? reducedTransition : panelTransition}
      data-state={expanded ? "expanded" : "collapsed"}
      data-reduce-motion={reduceMotion ? "true" : "false"}
      className={cn("group/animated-sidebar shrink-0", className)}
    >
      {children}
    </motion.aside>
  );
}

export function AnimatedSidebarLabel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): React.ReactElement {
  const { expanded, reduceMotion } = useAnimatedSidebar();

  return (
    <AnimatePresence initial={false}>
      {expanded ? (
        <motion.span
          animate={{ opacity: 1, x: 0 }}
          initial={{ opacity: 0, x: -5 }}
          exit={{
            opacity: 0,
            x: -5,
            transition: reduceMotion ? reducedTransition : labelExitTransition,
          }}
          transition={reduceMotion ? reducedTransition : labelEnterTransition}
          className={className}
        >
          {children}
        </motion.span>
      ) : null}
    </AnimatePresence>
  );
}

export function AnimatedSidebarSubmenu({
  children,
  className,
  open,
}: {
  children: ReactNode;
  className?: string;
  open: boolean;
}): React.ReactElement {
  const { expanded, reduceMotion } = useAnimatedSidebar();

  return (
    <AnimatePresence initial={false}>
      {expanded && open ? (
        <motion.div
          key="sidebar-submenu"
          animate="open"
          initial="closed"
          exit="closed"
          variants={getAnimatedSidebarSubmenuVariants(reduceMotion)}
          transition={reduceMotion ? { duration: 0 } : undefined}
          data-slot="sidebar-submenu"
          className={cn("overflow-hidden", className)}
        >
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export const AnimatedSidebarMenu = forwardRef<HTMLUListElement, HTMLAttributes<HTMLUListElement>>(
  function AnimatedSidebarMenu({ children, className, ...props }, forwardedRef) {
    return (
      <SharedLayoutBackground
        {...props}
        ref={forwardedRef as React.Ref<HTMLElement>}
        as="ul"
        inset={0}
        pillClassName="rounded-[8px] bg-subtle"
        pillContainerClassName="inset-y-auto top-0 h-[34px]"
        data-slot="sidebar-menu"
        className={cn("flex w-full min-w-0 list-none flex-col gap-1", className)}
      >
        {children}
      </SharedLayoutBackground>
    );
  },
);

export const AnimatedSidebarMenuItem = forwardRef<HTMLLIElement, HTMLMotionProps<"li">>(
  function AnimatedSidebarMenuItem({ className, ...props }, forwardedRef) {
    return (
      <motion.li
        {...props}
        ref={forwardedRef}
        layout="position"
        transition={animatedSidebarSpringLayout}
        data-slot="sidebar-menu-item"
        className={cn("relative", className)}
      />
    );
  },
);

export function AnimatedSidebarActiveBackground({
  active,
  className,
}: {
  active: boolean;
  className?: string;
}): React.ReactElement | null {
  const { activeLayoutId, reduceMotion } = useAnimatedSidebar();

  if (!active) return null;

  return (
    <motion.span
      aria-hidden
      layoutId={activeLayoutId}
      transition={reduceMotion ? { duration: 0 } : animatedSidebarSpringLayout}
      className={cn("pointer-events-none absolute inset-0 bg-sidebar-active", className)}
    />
  );
}
